import { useState } from 'react'
import { Outlet } from 'react-router'
import { Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useMunicipioActual } from '@/sesion/MunicipioActual'
import { guardarBandera, leerBandera } from '@/lib/preferencias'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import BarraUsuario from './BarraUsuario'
import Navegacion from './Navegacion'

/**
 * El panel autenticado: barra lateral, cabecera y la seccion abierta.
 *
 * Tres cosas de la barra lateral que se arreglaron el 10-sep-2026 mirando el
 * panel con la cartera real, y que no son de gusto:
 *
 * - **Se puede ocultar**, y la eleccion se recuerda entre visitas
 *   (localStorage, ver lib/preferencias.ts). En el mapa y en la cartera 15 rem
 *   de menu son 15 rem que no ve la tabla.
 * - **Tiene su propio scroll.** Con catorce secciones y una ventana baja, las
 *   ultimas quedaban debajo del borde y no habia forma de llegar a ellas.
 * - **No se mueve al hacer scroll en la pagina.** Antes se iba con el
 *   contenido: en una tabla de 41.000 deudas, cambiar de seccion obligaba a
 *   subir del todo primero. Ahora la barra y la cabecera se quedan fijas
 *   (`sticky`), y por eso la barra necesita `self-start`: un elemento de
 *   rejilla se estira a lo alto de su fila por omision, y estirado no le
 *   queda recorrido para pegarse.
 *
 * Con la barra oculta no se pierde la navegacion: el mismo panel lateral se
 * abre por encima con el boton del menu --el que en el telefono es la unica
 * forma-- y el boton de al lado la vuelve a fijar.
 */

/** Donde se recuerda si la barra lateral esta oculta. */
export const CLAVE_DEL_MENU = 'cobranza.menu-oculto'

export default function LayoutMunicipio() {
  const municipio = useMunicipioActual()
  const [menuAbierto, setMenuAbierto] = useState(false)
  const [menuOculto, setMenuOculto] = useState(() => leerBandera(CLAVE_DEL_MENU, false))

  function alternarElMenu() {
    const oculto = !menuOculto

    setMenuOculto(oculto)
    guardarBandera(CLAVE_DEL_MENU, oculto)
  }

  return (
    <div className={cn('min-h-dvh', menuOculto ? 'md:block' : 'md:grid md:grid-cols-[15rem_1fr]')}>
      {!menuOculto && (
        // El fondo y el borde van en la COLUMNA, que se estira con la pagina;
        // el aside es sticky y mide una ventana, asi que con una pagina larga
        // el fondo se cortaba a la altura de la ventana.
        <div className="hidden border-r bg-sidebar md:block">
          <aside className="sticky top-0 z-30 flex h-dvh flex-col">
            <div className="flex h-14 shrink-0 items-center border-b px-4 font-semibold">
              Cobranza Municipal
            </div>
            {/* min-h-0 es lo que deja que el hijo se encoja y aparezca su barra
                de desplazamiento: sin eso, un flex item no baja de su contenido. */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <Navegacion municipio={municipio} />
            </div>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-col">
        <BarraUsuario
          municipio={municipio}
          izquierda={
            <>
              <Sheet open={menuAbierto} onOpenChange={setMenuAbierto}>
                <SheetTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon"
                      // En el telefono siempre; en escritorio solo cuando la
                      // barra esta oculta, que es cuando hace falta.
                      className={menuOculto ? undefined : 'md:hidden'}
                      aria-label="Abrir el menú"
                    >
                      <Menu aria-hidden="true" />
                    </Button>
                  }
                />
                <SheetContent side="left" className="w-64 p-0">
                  <SheetHeader>
                    <SheetTitle>Cobranza Municipal</SheetTitle>
                  </SheetHeader>
                  <div className="overflow-y-auto">
                    <Navegacion
                      municipio={municipio}
                      alNavegar={() => {
                        setMenuAbierto(false)
                      }}
                    />
                  </div>
                </SheetContent>
              </Sheet>

              <Button
                variant="ghost"
                size="icon"
                className="hidden md:inline-flex"
                aria-expanded={!menuOculto}
                aria-label={menuOculto ? 'Fijar el menú lateral' : 'Ocultar el menú lateral'}
                onClick={alternarElMenu}
              >
                {menuOculto ? (
                  <PanelLeftOpen aria-hidden="true" />
                ) : (
                  <PanelLeftClose aria-hidden="true" />
                )}
              </Button>
            </>
          }
        />

        <main className="min-w-0 flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
